// Salésol — journal (P10). Runs on 'salesol:init' (S.boot). Motion System v1 only: the S.M tokens, S.onScroll, S.mark.
//  - "In this note" for a note ([data-toc][data-toc-note], article-note.liquid): h2s, with h3s nested under their h2; the
//    first heading is skipped when it opens the body or repeats the title. One list per [data-toc] (the sticky rail, and the
//    phone's disclosure), one IntersectionObserver per note: the row of the heading being read is aria-current="location"
//    (its gold dash scales, its text brightens: salesol.css, P10 block) and the rail shows that group's h3s. A row lands on
//    its heading (In-page landing): a smooth scroll ('auto' under reduced motion), then on scrollend (700 ms fallback) the
//    heading takes focus without scrolling and is marked (S.mark). In the disclosure, choosing a row closes it first.
//  - The reading line ([data-read-progress]): scaleX of the progress through the prose, in an S.onScroll pass (no listener
//    of its own). aria-hidden: the TOC's active row is its accessible equivalent.
//  - The journal's tag filter ([data-tag-scroller]): on a phone it is one scrolling row, and the active tag starts in view.
//  - Any other [data-toc] (P7's main-page chapter index, [data-toc-source]) is built exactly as before: a flat list of the
//    source's h2 and h3, ids note-1…, rows in the night ink that the page's own styles recolour.
const S = window.Salesol || (window.Salesol = {});
const norm = (s) => String(s || '').replace(/\s+/g, ' ').trim().toLowerCase();
const slug = (s) => norm(s).normalize('NFKD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 64);
const hdr = () => parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--hdr')) || 82;

// ---- the chapter index of any other page ([data-toc] without data-toc-note): unchanged
function initToc() {
  document.querySelectorAll('[data-toc]:not([data-toc-note])').forEach(toc => {
    if (toc.__init) return; toc.__init = 1;
    let src = null; try { src = document.querySelector(toc.dataset.toc || '[data-toc-source]'); } catch (e) {}
    const heads = src ? src.querySelectorAll('h2, h3') : [];
    const wrap = toc.closest('[data-toc-wrap]');
    if (!heads.length) { if (wrap) wrap.hidden = true; return; }
    heads.forEach((h, i) => { if (!h.id) h.id = `note-${i + 1}`; const row = document.createElement('a'); row.href = `#${h.id}`; row.style.cssText = 'display:flex;gap:10px;color:rgba(244,240,232,0.75);min-height:24px;'; const dash = document.createElement('span'); dash.style.cssText = 'flex:none;align-self:center;width:14px;height:1px;background:#e8b86b;'; row.append(dash, document.createTextNode(h.textContent.trim())); toc.appendChild(row); });
  });
}

// ---- In-page landing: smooth scroll, then focus (no scroll) and mark the heading on scrollend, 700 ms fallback
function land(h) {
  try { history.replaceState(history.state, '', `#${h.id}`); } catch (e) {}
  let done = false, t = 0;
  const fin = () => { if (done) return; done = true; clearTimeout(t); removeEventListener('scrollend', fin); if (!h.hasAttribute('tabindex')) h.setAttribute('tabindex', '-1'); try { h.focus({ preventScroll: true }); } catch (e) {} if (S.mark) S.mark(h); };
  const off = h.getBoundingClientRect().top - (hdr() + 22);
  if (Math.abs(off) < 2) { fin(); return; }
  if (S.reduced) { h.scrollIntoView({ behavior: 'auto', block: 'start' }); fin(); return; }
  if ('onscrollend' in window) { addEventListener('scrollend', fin); t = setTimeout(fin, 1600); } else t = setTimeout(fin, 700);
  h.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

// ---- a note's "In this note": every [data-toc-note] list of one body shares the headings, the ids and the active row
function buildNote(src, tocs) {
  const art = src.closest('article') || document; const h1 = art.querySelector('h1'); const title = norm(h1 && h1.textContent);
  let heads = [...src.querySelectorAll('h2, h3')].filter(h => norm(h.textContent));
  if (heads.length && (heads[0] === src.firstElementChild || (title && norm(heads[0].textContent).startsWith(title)))) heads = heads.slice(1);
  const wraps = tocs.map(t => t.closest('[data-toc-wrap]')).filter(Boolean);
  if (!heads.length) { wraps.forEach(w => { w.hidden = true; }); return; }
  /* ids from the words, so a link to a chapter survives an edit above it */
  const taken = new Set([...document.querySelectorAll('[id]')].map(e => e.id));
  heads.forEach(h => { if (h.id) return; const base = slug(h.textContent) || 'note'; let id = base, n = 2; while (taken.has(id)) id = `${base}-${n++}`; h.id = id; taken.add(id); });
  let g = -1; const items = heads.map(h => { const lv = h.tagName === 'H3' && g >= 0 ? 3 : 2; if (lv === 2) g++; return { h, lv, g, rows: [] }; });
  tocs.forEach(toc => {
    const frag = document.createDocumentFragment();
    items.forEach((it, i) => { const a = document.createElement('a'); a.href = `#${it.h.id}`; a.setAttribute('data-toc-row', ''); a.setAttribute('data-lv', String(it.lv)); a.dataset.i = String(i); const d = document.createElement('span'); d.setAttribute('data-toc-dash', ''); d.setAttribute('aria-hidden', 'true'); const tx = document.createElement('span'); tx.setAttribute('data-toc-text', ''); tx.textContent = it.h.textContent.trim(); a.append(d, tx); frag.append(a); it.rows.push(a); });
    toc.append(frag);
    toc.addEventListener('click', (e) => {
      const a = e.target.closest('a[data-toc-row]'); if (!a || e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button) return;
      const it = items[Number(a.dataset.i)]; if (!it) return; e.preventDefault();
      const d = toc.closest('details'); if (d) d.open = false;
      land(it.h);
    });
  });
  /* the count in the disclosure's summary ('In this note · 4') follows the rows actually listed */
  art.querySelectorAll('[data-toc-sum]').forEach(s => { const txt = s.textContent; const nx = txt.replace(/\d+(?=\D*$)/, String(items.length)); if (nx !== txt) s.textContent = nx; });
  /* the active row: the last heading above 40 % of the screen. Looked at again on every scroll frame (S.onScroll: pick in
     the read phase, apply in the write phase), so a jump that skips the band (an instant scrollTo, a scrollbar drag, Home,
     End or PageDown without smooth scrolling, find in page) still moves it. The observer's band (from under the header to
     40 %) adds the moves that come without a scroll: an image or the font landing above a heading */
  let cur = -2;
  const pick = () => { const lim = innerHeight * 0.4; let k = -1; for (let i = 0; i < items.length; i++) { if (items[i].h.getBoundingClientRect().top <= lim) k = i; else break; } return k; };
  const apply = (k) => {
    if (k === cur) return; cur = k; const gk = k >= 0 ? items[k].g : -1;
    items.forEach((it, i) => it.rows.forEach(a => { if (i === k) a.setAttribute('aria-current', 'location'); else a.removeAttribute('aria-current'); if (it.lv === 3) a.toggleAttribute('data-open', it.g === gk); }));
  };
  if ('IntersectionObserver' in window) { const io = new IntersectionObserver(() => apply(pick()), { rootMargin: `-${Math.round(hdr() + 22)}px 0px -60% 0px` }); items.forEach(it => io.observe(it.h)); }
  if (S.onScroll) S.onScroll(() => { const k = pick(); return k === cur ? null : () => apply(k); });
  apply(pick());
  /* a link to a chapter: the ids exist only now, so the browser's own jump on load found nothing */
  let want = ''; try { want = decodeURIComponent(location.hash.slice(1)); } catch (e) {}
  const hit = want && items.find(it => it.h.id === want); if (hit) requestAnimationFrame(() => hit.h.scrollIntoView({ block: 'start' }));
}
function initNoteToc() {
  const by = new Map();
  document.querySelectorAll('[data-toc][data-toc-note]').forEach(toc => {
    if (toc.__init) return; toc.__init = 1;
    let src = null; try { src = document.querySelector(toc.dataset.toc); } catch (e) {}
    if (!src) { const w = toc.closest('[data-toc-wrap]'); if (w) w.hidden = true; return; }
    if (!by.has(src)) by.set(src, []); by.get(src).push(toc);
  });
  by.forEach((tocs, src) => buildNote(src, tocs));
}

// ---- the reading line: p = how far through the prose (0 as it passes under the header, 1 as its end reaches the bottom of
// the screen), written as scaleX in S.onScroll's write phase. No transition: it follows the scroll, so reduced motion is the same
function initProgress() {
  document.querySelectorAll('[data-read-progress]').forEach(line => {
    if (line.__init) return; line.__init = 1;
    let prose = null; try { prose = document.querySelector(line.dataset.readProgress || '[data-prose]'); } catch (e) {}
    if (!prose || !S.onScroll) { line.hidden = true; return; }
    let last = -1;
    S.onScroll(() => {
      const r = prose.getBoundingClientRect(); const top = hdr(); const span = r.height - (innerHeight - top);
      const p = span > 0 ? Math.min(1, Math.max(0, (top - r.top) / span)) : (r.top < top ? 1 : 0);
      if (Math.abs(p - last) < 0.0005) return null;
      return () => { last = p; line.style.transform = `scaleX(${p.toFixed(4)})`; };
    });
  });
}

// ---- the journal's tag filter on a phone: one scrolling row; the active tag starts in view (no smooth, no page scroll)
function initTags() {
  document.querySelectorAll('[data-tag-scroller]').forEach(sc => {
    if (sc.__init) return; sc.__init = 1;
    const on = sc.querySelector('[aria-pressed="true"]'); if (!on || sc.scrollWidth <= sc.clientWidth + 1) return;
    const x = on.offsetLeft, w = on.offsetWidth; if (x < sc.scrollLeft || x + w > sc.scrollLeft + sc.clientWidth - 24) sc.scrollLeft = Math.max(0, x - 16);
  });
}

// ---- the journal's lead card: on intent (the pointer over it, focus, a touch) the note's cover is fetched in the size the note
// will ask for (data-warm-srcset / data-warm-sizes, the same srcset and sizes as the cover there), so it has landed by the time
// the route morph looks for it: the morph carries a cover over only once it is loaded. Not with saveData
function initWarm() {
  document.querySelectorAll('[data-warm-srcset]').forEach(a => {
    if (a.__warm) return; a.__warm = 1;
    const go = () => { if (a.__im) return; const c = navigator.connection; if (c && c.saveData) return; const im = new Image(); im.decoding = 'async'; im.sizes = a.dataset.warmSizes || '100vw'; im.srcset = a.dataset.warmSrcset; a.__im = im; };
    a.addEventListener('pointerenter', go, { passive: true }); a.addEventListener('touchstart', go, { passive: true }); a.addEventListener('focus', go);
  });
}

function run() { initToc(); initNoteToc(); initProgress(); initTags(); initWarm(); }
S.boot(run);
